import { initializeApp, deleteApp, getApps } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import {
  getAuth,
  signInWithPhoneNumber,
  ConfirmationResult,
  RecaptchaVerifier,
} from 'firebase/auth';

/** Legacy secondary app name (older builds); remove so Phone Auth uses default app only. */
const LEGACY_OTP_APP_NAME = 'nlsstore-phone-otp';

function removeOtpFirebaseApps() {
  getApps().forEach((app) => {
    if (app.name === '[DEFAULT]' || app.name === LEGACY_OTP_APP_NAME) {
      try {
        deleteApp(app);
      } catch {
        /* ignore */
      }
    }
  });
}

const isDevLog =
  typeof process !== 'undefined' && process.env.NODE_ENV !== 'production';

function devLog(...args: unknown[]) {
  if (isDevLog) console.log(...args);
}

function devWarn(...args: unknown[]) {
  if (isDevLog) console.warn(...args);
}

function logAuthErrorDetails(prefix: string, error: any) {
  console.error(prefix, {
    code: error?.code,
    message: error?.message,
    name: error?.name,
  });
  if (error?.customData != null) {
    try {
      console.error(prefix + ' customData (JSON)', JSON.stringify(error.customData, null, 2));
    } catch {
      console.error(prefix + ' customData (raw)', error.customData);
    }
  }
}
let auth: any = null;
let recaptchaVerifier: any = null;
let confirmationResult: ConfirmationResult | null = null;
let firebaseApp: any = null;
let lastInitFingerprint: string | null = null;
let otpDeliveryMode: 'client' | 'proxy' = 'client';
let proxySessionInfo: string | null = null;
let proxyPhoneE164: string | null = null;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

export type FirebaseWebClientConfig = {
  apiKey: string;
  projectId: string;
  appId?: string;
  messagingSenderId?: string;
  authDomain?: string;
  storageBucket?: string;
  measurementId?: string;
};

export type FirebaseOtpInitOptions = {
  deliveryMode?: 'client' | 'proxy';
  appCheckSiteKey?: string;
  appCheckDebugInDev?: boolean;
};

function configFingerprint(config: FirebaseWebClientConfig): string {
  return [
    (config.apiKey || '').trim(),
    (config.projectId || '').trim(),
    (config.appId || '').trim(),
    (config.messagingSenderId || '').trim(),
    (config.authDomain || '').trim(),
    (config.storageBucket || '').trim(),
    (config.measurementId || '').trim(),
  ].join('\u001f');
}

/**
 * When Admin only stores apiKey + projectId, Phone Auth still needs the Web app's
 * appId + messagingSenderId. For this store's historical Firebase project we ship
 * defaults so OTP works without extra Admin fields. Other projectIds must set Admin/env.
 */
const PHONE_AUTH_DEFAULTS_BY_PROJECT: Record<
  string,
  { appId: string; messagingSenderId: string }
> = {
  'narang-laptop-solution': {
    messagingSenderId: '642331786351',
    appId: '1:642331786351:web:c4a1802247f14c6351219a',
  },
};

function getRecaptchaSize(): 'invisible' | 'normal' | 'compact' {
  const fromEnv = (process.env.REACT_APP_FIREBASE_RECAPTCHA_SIZE || '').toLowerCase().trim();
  if (fromEnv === 'normal' || fromEnv === 'compact' || fromEnv === 'invisible') {
    return fromEnv;
  }
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') {
      return 'normal';
    }
  }
  return 'invisible';
}

const RECAPTCHA_SLOT_ATTR = 'data-firebase-recaptcha-slot';

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** Never set outer.innerHTML — gstatic reCAPTCHA then throws on null .style. */
function getOuterRecaptchaHost(
  containerId: string,
  size: 'invisible' | 'normal' | 'compact'
): HTMLElement {
  let outer = document.getElementById(containerId);
  if (!outer) {
    outer = document.createElement('div');
    outer.id = containerId;
    document.body.appendChild(outer);
  }
  outer.removeAttribute('aria-hidden');
  if (size === 'invisible') {
    outer.className =
      'firebase-recaptcha-host fixed bottom-0 right-0 z-[9998] h-px w-px overflow-visible opacity-0 pointer-events-none';
  } else {
    outer.className =
      'firebase-recaptcha-host fixed bottom-4 left-1/2 z-[9999] flex w-full max-w-md -translate-x-1/2 flex-col items-center justify-center gap-2 px-4';
  }
  return outer;
}

function removeRecaptchaSlots(outer: HTMLElement) {
  outer.querySelectorAll(`[${RECAPTCHA_SLOT_ATTR}]`).forEach((node) => {
    try {
      node.remove();
    } catch {
      /* ignore */
    }
  });
}

async function destroyRecaptchaVerifier(containerId = 'recaptcha-container') {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch {
      /* ignore */
    }
    recaptchaVerifier = null;
  }
  await delay(200);
  const outer = document.getElementById(containerId);
  if (outer) {
    removeRecaptchaSlots(outer);
  }
}

export const firebaseOTP = {
  /**
   * Initialize Firebase for Phone Auth. Prefer full Web snippet (appId + messagingSenderId)
   * from Admin; if omitted, known store projects use built-in defaults.
   */
  initialize(config: FirebaseWebClientConfig, options: FirebaseOtpInitOptions = {}) {
    otpDeliveryMode = options.deliveryMode === 'proxy' ? 'proxy' : 'client';
    const fp = configFingerprint(config);
    if (fp === lastInitFingerprint && auth && firebaseApp) {
      return;
    }

    try {
      removeOtpFirebaseApps();
    } catch {
      /* ignore */
    }
    firebaseApp = null;
    auth = null;
    this.clearSession();
    void destroyRecaptchaVerifier('recaptcha-container');

    const apiKey = (config.apiKey || '').trim();
    const projectId = (config.projectId || '').trim();
    let resolvedAppId = (config.appId || '').trim();
    let resolvedMessagingSenderId = (config.messagingSenderId || '').trim();

    if (!resolvedAppId || !resolvedMessagingSenderId) {
      const defaults = PHONE_AUTH_DEFAULTS_BY_PROJECT[projectId];
      if (defaults) {
        if (!resolvedAppId) resolvedAppId = defaults.appId;
        if (!resolvedMessagingSenderId) {
          resolvedMessagingSenderId = defaults.messagingSenderId;
        }
        devWarn(
          '[Firebase OTP] Using built-in Web app credentials for project',
          projectId,
          '(Admin appId / messagingSenderId empty). Add them in Admin if you create a new Web app.'
        );
      }
    }

    if (!resolvedAppId || !resolvedMessagingSenderId) {
      throw new Error(
        'Firebase Phone Auth needs Web app appId and messagingSenderId for this project. ' +
          'Either add them in Admin → OTP (Firebase), or set REACT_APP_FIREBASE_APP_ID / ' +
          'REACT_APP_FIREBASE_MESSAGING_SENDER_ID (shop .env) or FIREBASE_WEB_* on the API server.'
      );
    }

    const authDomain =
      (config.authDomain || '').trim() || `${projectId}.firebaseapp.com`;
    const storageBucket =
      (config.storageBucket || '').trim() ||
      `${projectId}.appspot.com`;

    const firebaseConfig: Record<string, string> = {
      apiKey,
      authDomain,
      projectId,
      storageBucket,
      messagingSenderId: resolvedMessagingSenderId,
      appId: resolvedAppId,
    };

    if (config.measurementId?.trim()) {
      firebaseConfig.measurementId = config.measurementId.trim();
    }

    devLog('[Firebase OTP] init', {
      projectId,
      authDomain,
      appId: `${resolvedAppId.slice(0, 12)}…`,
      deliveryMode: otpDeliveryMode,
      origin: typeof window !== 'undefined' ? window.location.origin : '(ssr)',
    });

    try {
      // Default app only: Phone Auth + Identity Toolkit expect the standard web client.
      firebaseApp = initializeApp(firebaseConfig as any);

      const appCheckSiteKey = (
        options.appCheckSiteKey ||
        process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY ||
        ''
      ).trim();
      if (appCheckSiteKey) {
        try {
          const g = globalThis as Record<string, unknown>;
          const dbg = (
            process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN || ''
          ).trim();
          const debugInDev =
            options.appCheckDebugInDev !== false &&
            process.env.NODE_ENV === 'development';
          if (debugInDev) {
            if (dbg === 'true' || dbg === '1' || !dbg) {
              g.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
            } else if (dbg.length > 4) {
              g.FIREBASE_APPCHECK_DEBUG_TOKEN = dbg;
            }
          }
          initializeAppCheck(firebaseApp, {
            provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
            isTokenAutoRefreshEnabled: true,
          });
          devLog(
            '[Firebase OTP] App Check initialized (reCAPTCHA Enterprise). Required when Firebase Console enforces App Check on Authentication.'
          );
        } catch (acErr) {
          devWarn('[Firebase OTP] App Check init failed (optional):', acErr);
        }
      } else if (process.env.NODE_ENV === 'development') {
        devLog(
          '[Firebase OTP] No REACT_APP_FIREBASE_APPCHECK_SITE_KEY. If App Check → Authentication is enforced, add that key (App Check → Web app → reCAPTCHA Enterprise) + REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN for localhost.'
        );
      }

      auth = getAuth(firebaseApp);
      auth.languageCode = 'en';
      lastInitFingerprint = fp;
    } catch (error) {
      console.error('Firebase init error:', error);
      lastInitFingerprint = null;
      throw error;
    }
  },

  /** Tear down widget safely (call after dev fallback or before unmount). */
  async teardownRecaptcha(containerId = 'recaptcha-container') {
    await destroyRecaptchaVerifier(containerId);
  },

  // Setup reCAPTCHA verifier in a fresh inner slot (never clear outer host with innerHTML)
  async setupRecaptcha(containerId: string) {
    if (!auth) {
      throw new Error(
        'Firebase Auth is not ready. In Admin → OTP (Firebase), save a valid API Key and Project ID, refresh this page, and try again.'
      );
    }

    await destroyRecaptchaVerifier(containerId);

    try {
      const size = getRecaptchaSize();
      const outer = getOuterRecaptchaHost(containerId, size);
      removeRecaptchaSlots(outer);

      const slot = document.createElement('div');
      const slotId = `${containerId}-widget-${Date.now()}`;
      slot.id = slotId;
      slot.setAttribute(RECAPTCHA_SLOT_ATTR, 'true');
      slot.className =
        size === 'invisible'
          ? 'h-px w-px overflow-hidden'
          : 'min-h-[78px] w-full flex items-center justify-center';
      outer.appendChild(slot);

      if (size !== 'invisible') {
        outer.style.pointerEvents = 'auto';
        devWarn(
          `[Firebase OTP] Using reCAPTCHA size="${size}" (localhost or REACT_APP_FIREBASE_RECAPTCHA_SIZE). Complete the checkbox if shown, then Send OTP again.`
        );
      }

      recaptchaVerifier = new RecaptchaVerifier(auth, slotId, {
        size,
        callback: () => {
          devLog('reCAPTCHA verified');
        },
        'expired-callback': () => {
          devLog('reCAPTCHA expired');
        },
      });
      await recaptchaVerifier.render();
      devLog('Recaptcha verifier created', { size, slotId });
    } catch (error) {
      console.error('Recaptcha setup error:', error);
      recaptchaVerifier = null;
      throw error;
    }
  },

  formatPhoneE164(phone: string): string {
    let formattedPhone = phone.replace(/\D/g, '');
    if (formattedPhone.length === 10) {
      formattedPhone = '+91' + formattedPhone;
    } else if (formattedPhone.length === 12 && formattedPhone.startsWith('91')) {
      formattedPhone = '+' + formattedPhone;
    } else if (!formattedPhone.startsWith('+')) {
      formattedPhone = '+' + formattedPhone;
    }
    return formattedPhone;
  },

  async sendOTPViaProxy(formattedPhone: string): Promise<{ success: boolean; message: string }> {
    if (!recaptchaVerifier) {
      throw new Error('reCAPTCHA is not ready. Refresh and try again.');
    }

    const recaptchaToken = await recaptchaVerifier.verify();
    const response = await fetch(`${API_BASE_URL}/otp/firebase-send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phone: formattedPhone,
        recaptchaToken,
      }),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      const err = new Error(data.message || 'Failed to send OTP via server.');
      (err as any).code = data.code;
      throw err;
    }

    proxySessionInfo = data.sessionInfo;
    proxyPhoneE164 = data.phone || formattedPhone;
    confirmationResult = null;

    devLog('[Firebase OTP] sent via API proxy');
    return {
      success: true,
      message: data.message || 'OTP sent. Enter the code from your phone.',
    };
  },

  getDeliveryMode(): 'client' | 'proxy' {
    return otpDeliveryMode;
  },

  setDeliveryMode(mode: 'client' | 'proxy') {
    otpDeliveryMode = mode === 'proxy' ? 'proxy' : 'client';
  },

  // Send OTP via Firebase
  async sendOTP(phone: string): Promise<{ success: boolean; message: string }> {
    if (!auth || !recaptchaVerifier) {
      throw new Error('Firebase not initialized. Call initialize() and setupRecaptcha() first.');
    }

    const formattedPhone = this.formatPhoneE164(phone);

    if (otpDeliveryMode === 'proxy') {
      return this.sendOTPViaProxy(formattedPhone);
    }

    try {
      devLog('Sending Firebase OTP to:', formattedPhone.replace(/\d(?=\d{4})/g, '•'));

      confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifier);
      proxySessionInfo = null;
      proxyPhoneE164 = null;

      devLog('Firebase OTP sent successfully');
      return {
        success: true,
        message: 'OTP sent via Firebase. Enter the code from your phone.',
      };
    } catch (error: any) {
      console.error('Firebase sendOTP error:', error);
      logAuthErrorDetails('[Firebase OTP] sendVerificationCode failed', error);

      let errorMessage = 'Failed to send OTP';
      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Invalid phone number format. Use 10-digit number.';
      } else if (error.code === 'auth/quota-exceeded') {
        errorMessage = 'SMS quota exceeded. Please try again later.';
      } else if (error.code === 'auth/captcha-check-failed') {
        errorMessage = 'Captcha verification failed. Please try again.';
      } else if (error.code === 'auth/argument-error') {
        errorMessage = 'Invalid parameters. Please refresh and try again.';
      } else if (error.code === 'auth/missing-client-identifier') {
        errorMessage = 'Firebase not configured properly.';
      } else if (
        error.code === 'auth/invalid-app-credential' ||
        error.code === 'auth/invalid-app-credentials'
      ) {
        console.error(
          '[Firebase OTP] auth/invalid-app-credential — checklist:\n' +
            '  0) If Firebase App Check enforces Authentication: set REACT_APP_FIREBASE_APPCHECK_SITE_KEY + REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN in shop .env, restart dev server.\n' +
            '  1) Google Cloud → APIs & Services → Credentials → your Browser API key → API restrictions: allow "Identity Toolkit API" (or "Don\'t restrict key" for dev).\n' +
            '  2) Same key → Application restrictions: HTTP referrers must include this exact origin (e.g. http://localhost:5000) or use None for dev.\n' +
            '  3) Firebase → Authentication → Settings → Authorized domains: add localhost, 127.0.0.1, and your live domain.\n' +
            '  4) Admin OTP: apiKey + appId + messagingSenderId must be from the SAME Web app (built-in defaults can be stale if you rotated the web app).\n' +
            '  5) reCAPTCHA Enterprise API enabled on the Google Cloud project (Console error "Failed to initialize reCAPTCHA Enterprise").\n' +
            '  6) Try http://127.0.0.1:PORT instead of localhost (known Google quirk 2024+).\n' +
            '  7) Or turn off App Check enforcement for Authentication in Firebase Console for local dev.'
        );
        errorMessage =
          'Phone sign-in failed (Firebase app credential). Open F12 → Console for the full checklist (try App Check env vars, Identity Toolkit API on the key, 127.0.0.1 instead of localhost).';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  },

  // Verify OTP
  async verifyOTP(code: string): Promise<{
    success: boolean;
    user?: any;
    error?: string;
    idToken?: string;
  }> {
    if (proxySessionInfo) {
      try {
        const response = await fetch(`${API_BASE_URL}/otp/firebase-verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionInfo: proxySessionInfo, code }),
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          return { success: false, error: data.message || 'Invalid OTP' };
        }
        devLog('Firebase OTP verified (proxy):', data.user?.uid);
        return {
          success: true,
          idToken: typeof data.idToken === 'string' ? data.idToken : undefined,
          user: {
            uid: data.user?.uid,
            phoneNumber: data.user?.phoneNumber || proxyPhoneE164,
          },
        };
      } catch (error: any) {
        return { success: false, error: error.message || 'Verification failed' };
      }
    }

    if (!confirmationResult) {
      throw new Error('No pending OTP verification');
    }

    try {
      const result = await confirmationResult.confirm(code);
      devLog('Firebase OTP verified:', result.user.uid);
      let idToken: string | undefined;
      try {
      idToken = await result.user.getIdToken(true);
      } catch (tokErr) {
        console.error('[Firebase OTP] getIdToken failed:', tokErr);
      }
      return {
        success: true,
        idToken,
        user: {
          uid: result.user.uid,
          phoneNumber: result.user.phoneNumber,
        },
      };
    } catch (error: any) {
      console.error('Firebase verifyOTP error:', error);

      let errorMessage = 'Invalid OTP';
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = 'Invalid OTP code';
      } else if (error.code === 'auth/code-expired') {
        errorMessage = 'OTP expired. Please request a new one.';
      }

      return { success: false, error: errorMessage };
    }
  },

  // Clear session (OTP state only; use teardownRecaptcha to remove widget)
  clearSession() {
    confirmationResult = null;
    proxySessionInfo = null;
    proxyPhoneE164 = null;
  },
};
